/**
 * Delivery Service
 * Handles pincode validation, delivery fee calculation, and admin pincode management
 */
import api from './api';

const deliveryService = {
  async getActivePincodes(orderType) {
    try {
      const params = orderType ? { orderType } : {};
      const response = await api.get('/delivery/pincodes', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch pincodes', data: [] };
    }
  },

  async getDeliveryFee(pincode, orderType) {
    try {
      const params = orderType ? { orderType } : {};
      const response = await api.get(`/delivery/fee/${pincode}`, { params });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch delivery fee', data: null };
    }
  },

  async getFeeTiers() {
    try {
      const response = await api.get('/delivery/tiers');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch fee tiers', data: [] };
    }
  },

  async getAllPincodes(orderType) {
    try {
      const params = orderType ? { orderType } : {};
      const response = await api.get('/delivery/admin/pincodes', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch pincodes', data: [] };
    }
  },

  async addPincode(pincode, orderType, area = '', distanceKm = 0) {
    try {
      const response = await api.post('/delivery/admin/pincodes', { pincode, orderType, area, distanceKm });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to add pincode' };
    }
  },

  async togglePincode(id) {
    try {
      const response = await api.patch(`/delivery/admin/pincodes/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to toggle pincode' };
    }
  },

  async updateDistance(id, distanceKm) {
    try {
      const response = await api.patch(`/delivery/admin/pincodes/${id}/distance`, { distanceKm });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to update distance' };
    }
  },

  async updateFee(id, deliveryFee) {
    try {
      const response = await api.patch(`/delivery/admin/pincodes/${id}/fee`, { deliveryFee });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to update fee' };
    }
  },

  async updateThreshold(id, deliveryThreshold) {
    try {
      const response = await api.patch(`/delivery/admin/pincodes/${id}/threshold`, { deliveryThreshold });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to update delivery threshold' };
    }
  },

  async removePincode(id) {
    try {
      const response = await api.delete(`/delivery/admin/pincodes/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to remove pincode' };
    }
  },

  // ── Delivery Zones (polygons inside a pincode) ────────────────────────────

  async checkAddress({ pincode, orderType, lat, lng }) {
    try {
      const response = await api.post('/delivery/check-address', { pincode, orderType, lat, lng });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to check serviceability', data: null };
    }
  },

  async listZones(pincodeId) {
    try {
      const response = await api.get(`/delivery/admin/pincodes/${pincodeId}/zones`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch zones', data: [] };
    }
  },

  async createZone(pincodeId, name, polygonGeoJson) {
    try {
      const response = await api.post(`/delivery/admin/pincodes/${pincodeId}/zones`, { name, polygonGeoJson });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to create zone' };
    }
  },

  async updateZone(zoneId, updates) {
    try {
      const response = await api.patch(`/delivery/admin/zones/${zoneId}`, updates);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to update zone' };
    }
  },

  async deleteZone(zoneId) {
    try {
      const response = await api.delete(`/delivery/admin/zones/${zoneId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to delete zone' };
    }
  },

  // ── Service Holidays ──────────────────────────────────────────────────────

  async getUpcomingHolidays() {
    try {
      const response = await api.get('/delivery/holidays');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch holidays', data: [] };
    }
  },

  async getAllHolidays() {
    try {
      const response = await api.get('/delivery/admin/holidays');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch holidays', data: [] };
    }
  },

  async createHoliday(date, reason = '', isPublic = true) {
    try {
      const response = await api.post('/delivery/admin/holidays', { date, reason, isPublic });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to create holiday' };
    }
  },

  async updateHoliday(id, updates) {
    try {
      const response = await api.patch(`/delivery/admin/holidays/${id}`, updates);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to update holiday' };
    }
  },

  async deleteHoliday(id) {
    try {
      const response = await api.delete(`/delivery/admin/holidays/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to delete holiday' };
    }
  },
  // ── Operational Hours ─────────────────────────────────────────────────────

  async getOperationalHours() {
    try {
      const response = await api.get('/delivery/operational-hours');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch operational hours', data: { openTime: '06:00', closeTime: '22:30' } };
    }
  },

  async updateOperationalHours(openTime, closeTime) {
    try {
      const response = await api.patch('/delivery/admin/operational-hours', { openTime, closeTime });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to update operational hours' };
    }
  },
};

export default deliveryService;